import { ExternalLink } from 'lucide-react';
import type { ComponentProps, MouseEvent } from 'react';
import { Link } from 'react-router-dom';

import { useNotification } from '@/context/NotificationProvider';
import { useAbrirAutos, useVaiParaNovaAba } from '@/hooks/useAbrirAutos';
import { abrirAutosEmNovaAba, urlAutosSlim } from '@/lib/slim';
import { cn } from '@/lib/utils';

interface LinkDosAutosProps extends Omit<ComponentProps<typeof Link>, 'to' | 'state'> {
  /** Número do processo (formatado ou não). */
  numero: string;
  /** `?classe=…`, `?doc=…` — o mesmo na aba nova. */
  search?: string;
  state?: unknown;
}

/**
 * Link para os autos de um processo: um `<a href>` de verdade (copiar o endereço,
 * leitor de tela, abrir pelo menu do navegador), e o Ctrl+clique, o ⌘+clique e o
 * botão do meio abrem os autos em outra aba no modo janela — uma por processo —, em
 * vez do aplicativo completo com o menu. Com a preferência "Abrir os autos sempre em
 * nova aba", o clique comum também (docs/PLANO_BANCADA.md, Bloco 2).
 */
/**
 * O número que abre os autos sempre em outra aba (modo janela, uma por processo), para
 * quem trabalha numa lista e não quer sair dela — a fila "Para distribuir". Clicar de
 * novo no mesmo processo reaproveita a aba dele.
 */
export function LinkDosAutosEmNovaAba({ numero, className }: { numero: string; className?: string }) {
  const { showError } = useNotification();
  const abrir = (e: MouseEvent) => {
    e.preventDefault();
    if (!abrirAutosEmNovaAba(numero)) showError('O navegador bloqueou a nova aba. Autorize os pop-ups deste site.');
  };
  return (
    <a
      href={urlAutosSlim(numero)}
      // 44 px de toque no celular; no computador, a altura do texto.
      className={cn('inline-flex min-h-11 items-center gap-1 underline-offset-4 hover:underline sm:min-h-0', className)}
      onClick={(e) => e.button === 0 && abrir(e)}
      onAuxClick={(e) => e.button === 1 && abrir(e)}
    >
      {numero}
      <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
      <span className="sr-only">(abre em nova aba)</span>
    </a>
  );
}

export function LinkDosAutos({ numero, search = '', state, onClick, onAuxClick, ...props }: LinkDosAutosProps) {
  const abrirAutos = useAbrirAutos();
  const vaiParaNovaAba = useVaiParaNovaAba();
  return (
    <Link
      {...props}
      to={`/app/processos/${encodeURIComponent(numero)}/autos${search}`}
      state={state}
      onClick={(e) => {
        onClick?.(e);
        if (e.defaultPrevented || e.button !== 0) return;
        // Na mesma aba, o próprio `Link` navega (com o `state`).
        if (!vaiParaNovaAba(e)) return;
        abrirAutos(numero, { search, evento: e });
      }}
      onAuxClick={(e) => {
        onAuxClick?.(e);
        if (e.defaultPrevented || e.button !== 1) return;
        abrirAutos(numero, { search, evento: e });
      }}
    />
  );
}
